import React, { ComponentProps, FC, useEffect, useRef, useState } from 'react';
import { OptionType, SelectV1 } from '@withjoy/joykit';
import { Toggle } from '@withjoy/joykit/components/SelectV1/styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type ModifiedSelectV1 = Omit<
  ComponentProps<typeof SelectV1>,
  | 'fluid'
  | 'closeMenuOnSelect'
  | 'closeMenuOnClear'
  | 'hideSelectedOptions'
  | 'searchable'
  | 'loadingMessage'
  | 'disabled'
  | 'maxDropdownHeight'
  | 'noOptionsMessage'
  | 'options'
  | 'value'
>;

interface TimeZonePickerProps extends ModifiedSelectV1 {
  disabled?: boolean;
  value?: string;
  zIndex?: number;
}

export const TimeZonePicker: FC<TimeZonePickerProps> = props => {
  const { onMenuClose, onMenuOpen, disabled, value, onChange, ...restProps } = props;

  const [options, setOptions] = useState<OptionType[]>([]);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isLoadedRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      if (!isLoadedRef.current) {
        // Dynamically import the constant data
        const { TIMEZONE_OPTIONS } = await import('./TimeZonePicker.constants');
        setOptions(TIMEZONE_OPTIONS);
        isLoadedRef.current = true;
      }
      if (options) {
        const option = options.find(option => option.value === value);
        if (option) {
          setSelectedOption(option);
        }
      }
    };
    loadData();
  }, [options, value]);

  const onOptionSelect = (option: OptionType | null) => {
    setSelectedOption(option);
    onChange?.(option);
  };

  const handleMenuOpen = useEventCallback(() => {
    setIsOpen(true);
    onMenuOpen?.();
  });

  const handleMenuClose = useEventCallback(() => {
    setIsOpen(false);
    onMenuClose?.();
  });

  return (
    <SelectV1
      {...restProps}
      options={options}
      zIndex={props.zIndex || 1}
      autoComplete="off"
      searchable={true}
      onChange={onOptionSelect}
      value={selectedOption}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      disabled={disabled}
      iconRight={
        <Toggle
          source="optionSelect"
          $rotate={isOpen}
        />
      }
    />
  );
};

TimeZonePicker.displayName = 'TimeZonePicker';
